import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X } from 'lucide-react';

const TransitionEditor = ({ 
  transition = null, // null for new transition
  sourceState,
  targetState = '',
  availableStates = [],
  availableGuards = [],
  availableEffects = [],
  onSave, 
  onCancel,
  inline = false 
}) => {
  const [event, setEvent] = useState(transition?.event || '');
  const [target, setTarget] = useState(transition?.target || targetState || '');
  const [guards, setGuards] = useState(transition?.guards?.map(g => g.name) || []);
  const [effects, setEffects] = useState(transition?.effects || []);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (transition) {
      setEvent(transition.event || '');
      setTarget(transition.target || '');
      setGuards(transition.guards?.map(g => g.name) || []);
      setEffects(transition.effects || []);
    }
  }, [transition]);

  const validate = () => {
    const errs = [];
    if (!event.trim()) {
      errs.push('Event name is required');
    }
    if (!/^[A-Z][A-Z0-9_]*$/.test(event)) {
      errs.push('Event name must be UPPERCASE with underscores');
    }
    if (!target) {
      errs.push('Target state is required');
    }
    setErrors(errs);
    return errs.length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    
    onSave({
      event,
      target,
      guards: guards.map(name => ({ name, predicate: name })),
      effects
    });
  };

  if (inline) {
    return (
      <div 
        className="absolute bg-[#1a1f3a] border-2 border-purple-500 rounded-lg p-3 shadow-xl z-50 w-80"
        data-testid="transition-editor-inline"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">{transition ? 'Edit Transition' : 'Create Transition'}</h3>
          <button onClick={onCancel} className="text-gray-500 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="text-xs text-gray-400 font-mono">
            {sourceState} → {target || '...'}
          </div>

          <div>
            <Label className="text-xs">Event</Label>
            <Input
              value={event}
              onChange={(e) => setEvent(e.target.value.toUpperCase())}
              placeholder="EVENT_NAME"
              className="mt-1 h-8 text-sm font-mono"
              autoFocus
              data-testid="transition-event-input"
            />
          </div>

          <div>
            <Label className="text-xs">Target State</Label>
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-full mt-1 h-8 px-2 text-sm bg-black/30 border border-white/10 rounded"
              data-testid="transition-target-select"
            >
              <option value="">Select target...</option>
              {availableStates.map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>

          {availableGuards.length > 0 && (
            <div>
              <Label className="text-xs">Guards (optional)</Label>
              <select
                multiple
                value={guards}
                onChange={(e) => setGuards(Array.from(e.target.selectedOptions, o => o.value))}
                className="w-full mt-1 h-16 px-2 text-xs bg-black/30 border border-white/10 rounded font-mono"
                size={3}
              >
                {availableGuards.map((guard) => (
                  <option key={guard} value={guard}>{guard}</option>
                ))}
              </select>
            </div>
          )}

          {availableEffects.length > 0 && (
            <div>
              <Label className="text-xs">Effects (optional)</Label>
              <select
                multiple
                value={effects}
                onChange={(e) => setEffects(Array.from(e.target.selectedOptions, o => o.value))}
                className="w-full mt-1 h-16 px-2 text-xs bg-black/30 border border-white/10 rounded font-mono"
                size={3}
              >
                {availableEffects.map((effect) => (
                  <option key={effect} value={effect}>{effect}</option>
                ))}
              </select>
            </div>
          )}

          {errors.length > 0 && (
            <div className="text-xs text-red-400 space-y-1">
              {errors.map((err, idx) => (
                <div key={idx}>• {err}</div>
              ))}
            </div>
          )}

          <div className="flex space-x-2 pt-2">
            <Button 
              onClick={handleSave} 
              size="sm" 
              className="flex-1 h-8 text-xs"
              data-testid="transition-save-button"
            >
              {transition ? 'Update' : 'Create'}
            </Button>
            <Button 
              onClick={onCancel} 
              size="sm" 
              variant="outline" 
              className="h-8 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Bottom sheet implementation similar to StateEditor
  return null;
};

export default TransitionEditor;
